/**
 * ログイン用パスキー（出金用とは別の表・別の登録）のブラウザ側の呼び出し（2026-09-28 creator/passkey-login）。
 * サーバーの正: apps/server/src/modules/platform/loginPasskeyRoutes.ts（形は Core と Creator で合意済み・docs/teams/creator/tasks/passkey-login.md）。
 *
 * 使う所: /login のボタン（components/OAuthButtons.tsx）・/account のログイン方法（pages/AccountLoginPasskeys.tsx）・
 *   再認証の部品の「パスキーで確認」（components/Reauth.tsx）。
 * 判断（やり直し・エラーの分け方）は loginOptions.ts（node --test で確かめる）。ここは api.ts と passkey.ts をつなぐだけ。
 * base64url ↔ バイト列の変換は passkey.ts（moneyLogic.ts の toBase64Url / fromBase64Url）を使う = 同じ物を 2 つ作らない（指示書 4）。
 */
import { api, type Developer } from './api';
import { signInWithPasskey, type PasskeyRequestJson } from './loginOptions';
import { createPasskey, getPasskeyAssertion } from './passkey';

/** 一覧の 1 行（GET /v1/developers/me/login-passkeys） */
export interface LoginPasskey {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}
export interface LoginPasskeysResponse {
  passkeys: LoginPasskey[];
}

// 1 人が持てる数はここに写さない（2026-10-05 console/followups）。正はサーバーの上限だけで、断られたときの 409 too_many_passkeys の
// details.limit を loginOptions.ts の passkeyLimitFrom で読む（以前の MAX_LOGIN_PASSKEYS = 10 はサーバーが 50 に上げた後も 10 本で止めていた）

/** /login のボタン: options → 端末のパスキーを選ぶ → { token, developer }（期限切れは 1 回だけやり直す = loginOptions.ts） */
export function passkeySignIn(): Promise<{ token: string; developer: Developer }> {
  return signInWithPasskey({
    post: (path, body) => api<unknown>(path, { method: 'POST', body, auth: false }),
    getAssertion: (publicKey) => getPasskeyAssertion(publicKey),
    cast: (r) => r as { token: string; developer: Developer }
  });
}

/**
 * 再認証をログイン用パスキーで（部品の「パスキーで確認」）: options → get → { reauthToken }（5 分・一回限り・このセッション専用）。
 * 得たトークンはパスワードと同じ強さの証明として、閉鎖・ログイン方法の追加 / 解除・出金用パスキーの登録 / 削除の本文に足せる（サーバーの lib/developerReauth.ts）
 */
export async function reauthWithLoginPasskey(): Promise<{ reauthToken: string; expiresAt: string }> {
  const o = await api<{ publicKey: PasskeyRequestJson }>('/v1/developers/me/login-passkeys/reauth/options', { method: 'POST', body: {} });
  const credential = await getPasskeyAssertion(o.publicKey);
  return api<{ reauthToken: string; expiresAt: string }>('/v1/developers/me/login-passkeys/reauth', { method: 'POST', body: { credential } });
}

/** 登録用の options（create 用）。メール未確認は 403 email_unverified・上限で 409 too_many_passkeys（details.limit） */
export function loginPasskeyCreationOptions() {
  return api<{ publicKey: Parameters<typeof createPasskey>[0] }>('/v1/developers/me/login-passkeys/options', { method: 'POST', body: {} });
}

/** 認証器で作る（クリックの中で呼ぶ。Safari は操作の直後でないと断ることがある） */
export const createLoginPasskey = (publicKey: Parameters<typeof createPasskey>[0]) => createPasskey(publicKey);

/** 作った資格情報を登録する（再認証の証明を本文に足す。足りなければ 400 reauthentication_required） */
export function registerLoginPasskey(body: { credential: Awaited<ReturnType<typeof createPasskey>>; label?: string; password?: string; reauthToken?: string }) {
  return api<{ passkey: LoginPasskey }>('/v1/developers/me/login-passkeys', { method: 'POST', body });
}

/** 外す（再認証の証明つき）。最後のログイン手段は 409 last_login_method。成功するとほかのセッションと MCP の接続（kdt_）は失効する */
export function removeLoginPasskey(id: string, proof: { password?: string; reauthToken?: string }) {
  return api<LoginPasskeysResponse>(`/v1/developers/me/login-passkeys/${encodeURIComponent(id)}`, { method: 'DELETE', body: proof });
}
